import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { changeCheckResultAtom } from '@/store/polling-atoms';
import type { ChangeCheckRes } from '@/types/api/message/change-check';

export type ChangeCheckKey = keyof ChangeCheckRes;

/**
 * 订阅 change-check 特定字段的变化
 * Subscribe to change-check specific field changes
 *
 * 当字段值为 1 时，执行回调函数
 * When field value is 1, execute callback function
 *
 * @param key - 要订阅的字段 (Field to subscribe to)
 * @param callback - 字段变化时的回调 (Callback when field changes)
 * @param enabled - 是否启用订阅，默认 true (Whether to enable subscription, default true)
 *
 * @example
 * ```typescript
 * // 订阅余额变化 (Subscribe to balance changes)
 * useChangeSubscription('balance', () => {
 *   refetch(); // 重新获取余额 (Refetch balance)
 * }, isLogin && !paused);
 * ```
 */
export function useChangeSubscription(
  key: ChangeCheckKey,
  callback: () => void,
  enabled: boolean = true
) {
  // 使用 ref 避免回调变化导致的重新订阅
  // Use ref to avoid re-subscription due to callback changes
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // 直接订阅 changeCheckResultAtom，确保每次轮询结果更新时都会重新检查
  // Directly subscribe to changeCheckResultAtom to ensure re-check on every polling update
  const changeCheckResult = useAtomValue(changeCheckResultAtom);

  // 监听 changeCheckResultAtom 变化，当对应字段值为 1 时触发回调
  // Listen to changeCheckResultAtom changes, trigger callback when field value is 1
  useEffect(() => {
    if (enabled && changeCheckResult?.[key] === 1) {
      try {
        callbackRef.current();
      } catch (error) {
        console.error(`[useChangeSubscription] Error in ${key} callback:`, error);
      }
    }
  }, [key, changeCheckResult, enabled]);
}
